<script setup lang="ts">
import GlobalErrorModal from '@/components/error/GlobalErrorModal.vue'
import { PublicClientApplication } from '@azure/msal-browser'
import { getAppEnvironment } from '@/environments'

const url = useRequestURL()
// 'noindex' tag for non-production/non-indexable hosts
const noindexHosts = ['pawprotect.embracepetinsurance.com', 'myembrace-rc.embracepetinsurance.com']
const meta = noindexHosts.includes(url.host) ? [{ name: 'robots', content: 'noindex' }] : []

useHead({
  meta,
  script: [
    {
      src: 'https://js.stripe.com/v3/',
      async: true
    }
  ]
})

// Handle MSAL OAuth redirects globally at app level
// This MUST happen before any routing/navigation occurs
if (import.meta.client) {
  const handleMsalRedirect = async () => {
    try {
      // Check if this is an OAuth callback (has code & state params)
      // OAuth params can be in either query string OR hash fragment
      const searchParams = new URLSearchParams(window.location.search)
      const hashParams = new URLSearchParams(window.location.hash.substring(1))
      const hasOAuthParams =
        (searchParams.has('code') && searchParams.has('state')) || (hashParams.has('code') && hashParams.has('state'))

      if (!hasOAuthParams) return

      const environment = getAppEnvironment()
      const msalInstance = new PublicClientApplication({
        auth: environment.msal.auth,
        cache: environment.msal.cache
      })

      await msalInstance.initialize()

      const response = await msalInstance.handleRedirectPromise()

      if (response && response.accessToken) {
        // Store the token response in sessionStorage for the admin-token page to retrieve
        sessionStorage.setItem(
          'msal_token_response',
          JSON.stringify({
            accessToken: response.accessToken,
            expiresOn: response.expiresOn?.toISOString(),
            account: {
              username: response.account?.username,
              name: response.account?.name
            }
          })
        )

        // Navigate to admin-token page (or the stored redirect path)
        const redirectPath = sessionStorage.getItem('msal_redirect_path') || '/admin-token'
        sessionStorage.removeItem('msal_redirect_path')

        await navigateTo(redirectPath, { replace: true })
      }
    } catch {
      // Silent fail - error will be handled by admin-token page
    }
  }

  // Execute immediately on app mount
  handleMsalRedirect()
}
</script>

<template>
  <div class="h-full">
    <DialogInactivity />
    <GlobalErrorModal />
    <ErrorRenewalTooCloseErrorModal />
    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>
  </div>
</template>
